import React, { useState } from 'react';
import { generateTaskSeparation } from '../services/geminiService';
import { TaskSeparationAnalysis } from '../types';
import { Scissors, RefreshCw, Shield } from 'lucide-react';
import { useUser } from '../context/UserContext';

const CourageSeparation: React.FC = () => {
  const [conflict, setConflict] = useState('');
  const [analysis, setAnalysis] = useState<TaskSeparationAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp, unlockBadge } = useUser();

  const handleAnalyze = async () => {
    if (!conflict.trim()) return;
    setLoading(true);
    setAnalysis(null);
    try {
      const data = await generateTaskSeparation(conflict);
      setAnalysis(data);
      addXp(40, 'Separated Tasks');
      unlockBadge('task_separator');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-red-50 text-red-600 rounded-full mb-4">
          <Scissors size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">Task Separation</h1>
        <p className="text-nunchi-600">
          "All interpersonal relationship troubles are caused by intruding on other people's tasks." <br/>
          Identify whose task it is to find peace.
        </p>
      </header>

      {!analysis && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                Describe the conflict
            </label>
            <textarea
              value={conflict}
              onChange={(e) => setConflict(e.target.value)}
              placeholder="e.g., My parents want me to be a doctor but I want to be an artist, My friend is mad at me for not lending money..."
              className="w-full h-32 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-red-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handleAnalyze}
              disabled={!conflict.trim()}
              className="w-full bg-red-700 text-white py-4 rounded-xl font-bold hover:bg-red-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Scissors size={18} /> Separate Tasks
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="w-16 h-16 border-4 border-red-100 border-t-red-600 rounded-full animate-spin mx-auto mb-4"></div>
             <p className="font-serif italic text-red-700">Untangling the threads...</p>
        </div>
      )}

      {analysis && (
        <div className="animate-fadeIn space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-xl border border-nunchi-200 shadow-sm">
                    <h3 className="text-nunchi-400 font-bold uppercase tracking-wider text-xs mb-2 flex items-center gap-2">
                        <Shield size={14} /> Your Task
                    </h3>
                    <p className="text-nunchi-800 font-medium">{analysis.yourTask}</p>
                </div>
                 <div className="bg-red-50 p-6 rounded-xl border border-red-100 shadow-sm opacity-80">
                    <h3 className="text-red-400 font-bold uppercase tracking-wider text-xs mb-2">Their Task</h3>
                    <p className="text-red-900 font-medium">{analysis.theirTask}</p>
                </div>
            </div>

            <div className="bg-white border border-nunchi-200 rounded-2xl p-8 shadow-sm">
                 <h3 className="text-nunchi-400 uppercase tracking-widest text-xs font-bold mb-3">Adlerian Advice</h3>
                 <p className="text-lg leading-relaxed font-serif text-nunchi-800 mb-6">
                     "{analysis.advice}"
                 </p>
                 <div className="bg-red-50 p-4 rounded-lg text-center border border-red-100">
                     <p className="text-xs text-red-400 uppercase font-bold mb-1">Your Mantra</p>
                     <p className="text-lg font-bold text-red-700">"{analysis.mantra}"</p>
                 </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={() => { setAnalysis(null); setConflict(''); }}
                  className="text-red-600 hover:text-red-800 font-bold flex items-center gap-2 mx-auto"
                >
                    <RefreshCw size={16} /> Separate Another Conflict
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default CourageSeparation;